/*
* Prepare bids dash for authenticated users
*/

import './dashboard.css';
import '@progress/kendo-ui';

import { Grid } from '@progress/kendo-grid-react-wrapper';
import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import VirtualizedSelect from 'react-virtualized-select';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';
import * as JSZip from 'jszip';
import fileDownload from 'js-file-download';

import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { FormUtilities } from '../common/formutilities';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { OrderStatuses } from '../order/orderpricing';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import * as MutateActions from '../actions/mutate';
import * as ReduxQueries from '../queries';
import ApiEndpoints from '../config/apiendpoints';
import { userHasRole } from 'src/utils/userUtils';

(window as any).JSZip = JSZip;

export interface IPreparedBidsSearch extends AppComp.IAppComponentProps {
    filterByRegions: any;
    filterByStatuses: any;
    filterByUsers: any;
    navigateTo?: any;
    preparedBidsFilterManager?: any;
    preparedBidsFilterStatusId?: any;
    preparedBidsFilterRegionId?: any;
    downloadKML?: any;
}

class PreparedBidsSearch extends AppComp.AppComponent<IPreparedBidsSearch, any> {
    public MomentNow: any = moment();
    public isGridExporting: boolean = false;

    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 50, 1),
            GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 210, 2,
                                                    GridUtilities.kendoGridLinkColumnTemplate('associationName', 'associationDetailLinkHref', this.appText('association-detail-link-title')),
                                                    {
                                                        aggregates: ['count'],
                                                        footerTemplate: `#=count# ${this.appTextResults()}`,
                                                    }),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapAddress1', 1003),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapCity', 1004),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapState', 1005),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapZip', 1006),
            GridUtilities.kendoGridColumnDefinition('associationLocation', this.appTextGridColumnHeader('location'), 110, 3),
            GridUtilities.kendoGridColumnDefinition('statusName', this.appTextGridColumnHeader('status'), 130, 6, ''),
            GridUtilities.kendoGridColumnDefinition('managerUser', this.appTextGridColumnHeader('manager'), 60, 7),
            GridUtilities.kendoGridColumnDefinition('coordinatorUser', this.appTextGridColumnHeader('coordinator'), 80, 8),
            GridUtilities.kendoGridColumnDefinition('bidDateValue', this.appTextGridColumnHeader('bid-date'), 70, 9, undefined, { format: '{0:d}' }),
            GridUtilities.kendoGridColumnDefinition('viewOrderLink', '', 80, 12,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewOrderLink', 'viewOrderLinkHref', this.appText('view-bid-link-title')),
                                                    { headerTemplate: '' }),
            GridUtilities.kendoGridColumnDefinition('viewInventoryLink', '', 90, 13,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewInventoryLink', 'viewInventoryLinkHref', this.appText('view-inventory-link-title')),
                                                    { headerTemplate: '' }),
        ],
        this.appText('no-results'),
        {
            saveChanges: (e) => { this.handleExportKML(e); },
            excelExport: this.onGridExcelExport,
            editable: true,
            toolbar: [
                'excel',
                {
                    name: 'save',
                    text: 'Export to Google Earth',
                    iconClass: 'k-icon k-i-globe'
                },
                'pdf',
            ]
        },
        this.appTextPageTitle()
    );

    public constructor(props: IPreparedBidsSearch) {
        super(props, 'app.prepared-bids.');
    }

    public componentDidMount() {                    
        const enforce = this.enforceAdminOrEmployeeOnly();

        if (enforce) { this.props.navigateTo(NavigationLinks.clientDashboardHome()); }
    }

    public onGridExcelExport(e: any) {
        if (!this.isGridExporting) {
            e.sender.showColumn(2);
            e.sender.showColumn(3);
            e.sender.showColumn(4);
            e.sender.showColumn(5);

            e.preventDefault();

            this.isGridExporting = true;

            e.sender.saveAsExcel();
        } else {
            e.sender.hideColumn(2);
            e.sender.hideColumn(3);
            e.sender.hideColumn(4);
            e.sender.hideColumn(5);

            this.isGridExporting = false;
        }
    }

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return records.map( r => {
            let record = _.cloneDeep(r);

            // Association name
            record.associationName = _.get(record, 'association.name', '');

            // Association name link details
            record.associationDetailLinkHref = (record.associationId) ? NavigationLinks.associationDetail(record.associationId) : '';

            // Association Location
            record.associationLocation = (record.association && record.association.location) ? Formatters.composeLocationCityState(record.association.location) : '';

            // Association Location Details for Map Export
            record.associationLocationMapAddress1 = Formatters.getStr(record, 'association.location.address1');
            record.associationLocationMapCity = Formatters.getStr(record, 'association.location.city');
            record.associationLocationMapState = Formatters.getStr(record, 'association.location.stateCode');
            record.associationLocationMapZip = Formatters.getStr(record, 'association.location.postalCode');

            // Service level name
            record.serviceLevelName = _.get(record, 'serviceLevel.name', '');

            // Status name
            record.statusName = _.get(record, 'status.name', '');

            // Manager
            record.managerUser = _.get(record, 'user.username', '');

            // Coordinator
            record.coordinatorUser = _.get(record, 'siteInspector.username', '');

            // Bid date
            record.bidDateValue = record.bidDate ? new Date(record.bidDate) : '';

            // Start date
            record.startDateValue = record.startDate ? new Date(record.startDate) : '';

            // Due date
            record.dueDateValue = record.dueDate ? new Date(record.dueDate) : '';

            // Calculate number of days & weeks until due
            record.numDays = record.dueDateValue ? moment(record.dueDateValue).diff(this.MomentNow, 'days') : '';
            record.numDaysSort = AppUtilities.isNumeric(record.numDays) ? Number(parseInt(record.numDays, 10)) : -99999;
            record.numWeeks = record.dueDateValue ? moment(record.dueDateValue).diff(this.MomentNow, 'weeks') : -1;

            // View order link
            record.viewOrderLink = '';
            record.viewOrderLinkHref = '';
            if (record.orderId) {
                record.viewOrderLink = this.appText('view-bid');
                // record.viewOrderLinkHref = NavigationLinks.orderEdit(record.orderId);
                record.viewOrderLinkHref = NavigationLinks.orderDetail(record.orderId);
            }

            // View inventory link
            record.viewInventoryLink = '';
            record.viewInventoryLinkHref = '';
            if (record.association && record.associationId) {
                record.viewInventoryLink = this.appText('view-inventory');
                record.viewInventoryLinkHref = NavigationLinks.associationInventory(record.associationId);
            }

            return record;
        });
    }

    public handleSelectManager(e: any) {
        const value = e ? e.value : '';
        this.props.preparedBidsFilterManager(value);
    }

    public handleSelectStatusId(e: any) {
        const value = e ? e.value : 0;
        this.props.preparedBidsFilterStatusId(value);
    }

    public handleSelectRegionId(e: any) {
        const value = e ? e.value : 0;
        this.props.preparedBidsFilterRegionId(value);
    }

    public handleExportKML(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            {
                orders: _.map(_.get(this.props, 'search.results', []), (o) => {
                    const placemarkAddress = `${_.get(o, 'association.location.address1')} ${_.get(o, 'association.location.city')}, ${_.get(o, 'association.location.stateCode')} ${_.get(o, 'association.location.postalCode')}`;
                    const placemarkDescription = `Order #${_.get(o, 'orderId')}`;

                    return {
                        placemarkAddress: placemarkAddress,
                        placemarkDescription: placemarkDescription,
                        name: Formatters.getStr(o, 'association.name'),
                        address: Formatters.getStr(o, 'association.location.address1'),
                        city: Formatters.getStr(o, 'association.location.city'),
                        stateCode: Formatters.getStr(o, 'association.location.stateCode'),
                        postalCode: Formatters.getStr(o, 'association.location.postalCode'),
                        associationId: Formatters.getInt(o, 'associationId'),
                        orderId: Formatters.getInt(o, 'orderId'),
                    };
                })
            },
            {
                kmlConfig: {
                    folderName: 'Prepare Bids',
                }
            }
        );

        this.props.downloadKML(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {

                const kmlFilename = `prepare-bids-${moment().format('l LT')}.kml`;
                const kmlData = Formatters.getStr(response, 'text');

                fileDownload(kmlData, kmlFilename);
            } else {
              // Error during creation...
            }
        });
    }

    public render() {
        const { search, filterByUsers, filterByRegions, filterByStatuses, ...rest } = this.props;
        
        const rows = this.extendGridData(search.results);
        const dataSource = GridUtilities.kendoGridDataSourcePaged(rows, {
            aggregate: [
                { field: 'associationName', aggregate: 'count' },
                { field: 'acceptedPrice', aggregate: 'sum' }
            ],
            sort: {
                dir: 'asc',
                field: 'numDaysSort',
            }
        });

        // Prepare select dropdown options
        let extendedUsers = [];
        if (AppUtilities.isArrayWithLength(filterByUsers)) {
            extendedUsers = filterByUsers.map(c => {
                c.sort = _.get(c, 'contact.firstName', '').trim().toLowerCase();
                if (!c.label) { c.label = Formatters.composeUsernameLabel(c, true); }

                return c;
            });
        }
        const filterOptionAny = this.appTextFilterOptionAny();
        const users = FormUtilities.getExtendedFilterOptions(_.filter(extendedUsers, ((u) => userHasRole(u, 'administrator'))), 'username', 'sort', filterOptionAny, '', '_');
        const regions = FormUtilities.getExtendedFilterOptions(filterByRegions, 'regionId', 'label', filterOptionAny, -1);
        const statuses = FormUtilities.getExtendedFilterOptions(filterByStatuses, 'statusId', 'statusId', this.appText('filter-option-status-prepared-bids'), -1);

        const isAdmin = (this.enforceAdminOnly()) ? false : true;
        const dropDisabled = false;

        const pageTitle = this.appTextResultsAndPageTitle(rows.length, this.commonMessagePaths.searchResults, false, 'list-of-prepared-bids', true);

        const page = (
            <WhiteBoxPageSection title={pageTitle}>
                <Row>
                    <Col xs={12}>
                        <form onSubmit={e => { e.preventDefault(); }}>
                            <Row className="form-group">

                                <Col xs={2} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-manager')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={users.filter}
                                        name="manager-filter"
                                        onChange={e => this.handleSelectManager(e)}
                                        options={users.options}
                                        value={search.filters.manager}
                                        disabled={dropDisabled}
                                    />
                                </Col>
                            </Row>

                            <Row className="form-group">
                                <Col xs={2} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-status-id')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={statuses.filter}
                                        name="status-filter"
                                        onChange={e => this.handleSelectStatusId(e)}
                                        options={statuses.options}
                                        value={search.filters.statusId}
                                    />
                                </Col>

                                <Col xs={2} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-region-id')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={regions.filter}
                                        name="region-filter"
                                        onChange={e => this.handleSelectRegionId(e)}
                                        options={regions.options}
                                        value={search.filters.regionId}
                                    />
                                </Col>
                            </Row>
                        </form>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <div className="home-dashboard-table-wrapper">
                            <Grid dataSource={dataSource} {...this.gridOptions} />
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle /* 20200220 "" */} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getPreparedBidsSearchUrl();

const mapPropsToConfigs = (props) => ReduxQueries.Queries.searchPreparedBidsRequest(
    getApiUrl(),
    ReduxQueries.PropsAccessToken(props),
    _.get(props, 'search.filters.manager', ''),
    _.get(props, 'search.filters.regionId', -1),
    _.get(props, 'search.filters.statusId', -1),
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        filterByRegions: state.entities.searchPreparedBidsRegions,
        filterByStatuses: state.entities.searchPreparedBidsStatuses,
        filterByUsers: state.entities.searchPreparedBidsUsers,
        search: {
            results: state.entities.searchPreparedBidsResults,
            filters: {
                manager: state.searchFiltersPreparedBids.manager,
                regionId: state.searchFiltersPreparedBids.regionId,
                statusId: state.searchFiltersPreparedBids.statusId,
            }
        },
    });
};

const mapDispatchToProps = dispatch => ({
    navigateTo: (path) => dispatch(push(path)),
    preparedBidsFilterManager: (v) => dispatch(Actions.preparedBidsFilterManager(v)),
    preparedBidsFilterStatusId: (v) => dispatch(Actions.preparedBidsFilterStatusId(v)),
    preparedBidsFilterRegionId: (v) => dispatch(Actions.preparedBidsFilterRegionId(v)),
    downloadKML: (d: any, a: string, o: boolean) => dispatch(MutateActions.downloadKMLMutateAsync(d, a, o)),
});

const PreparedBidsSearchContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
  )(PreparedBidsSearch);

export default withRouter(PreparedBidsSearchContainer);



// WEBPACK FOOTER //
// ./src/dashboard/prepared-bids.tsx